import { Select } from 'antd';
import React, { useState, useEffect, useCallback } from 'react';
import { Switch } from '@headlessui/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  LinkIcon,
  BanknotesIcon,
  ArrowUturnLeftIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  CalendarDaysIcon,
  ClockIcon,
  PuzzlePieceIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';
import { SkeletonBlock } from '../../../../components/skeleton/skeleton';
import storeApi from '../../../../services/storeApi';
import companyApi from '../../../../services/companyApi';
import companySettingApi from '../../../../services/companySettingApi';
import timezoneApi from '../../../../services/timezoneApi';
import { fetchCompanySettings } from '../../../../store/slices/companySlice';
import { toSlug, SLUG_MAX_LENGTH } from '../../../../utils/slug';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export const SETTING_CODES = {
  PAYOUT_PAUSED: 'payout_paused',
  FINANCE_V2_ENABLED: 'finance_v2_enabled',
  PAYOUT_PERIOD_TYPE: 'payout_period_type',
  PAYOUT_PERIOD_INTERVAL_DAYS: 'payout_period_interval_days',
  PAYOUT_PERIOD_ANCHOR: 'payout_period_anchor',
  PAYOUT_PERIOD_TIMEZONE: 'payout_period_timezone',
  EARLY_PAYOUT_FEE_TYPE: 'early_payout_fee_type',
  EARLY_PAYOUT_FEE_RATE: 'early_payout_fee_rate',
  EARLY_PAYOUT_FEE_AMOUNT: 'early_payout_fee_amount',
  REFUND_PAUSED: 'refund_paused',
  REFUND_FRAUD_ENABLED: 'refund_fraud_enabled',
  REFUND_FRAUD_MODE: 'refund_fraud_mode',
  REFUND_FRAUD_DAYS: 'refund_fraud_days',
  REFUND_FRAUD_ORDER_COUNT: 'refund_fraud_order_count',
  REFUND_FRAUD_THRESHOLD_PERCENT: 'refund_fraud_threshold_percent',
  KITCHEN_TIPS_HIDDEN: 'kitchen_tips_hidden',
};

// Default fee when no setting is stored — kept in sync with PayoutController.EarlyPayout.
const DEFAULT_EARLY_PAYOUT_FEE_PERCENT = 3;

// Store format is a decimal rate ("0.03"); UI format is a percent ("3").
const decimalRateToPercent = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const rate = Number(value);
  if (!Number.isFinite(rate)) return '';
  return String(+(rate * 100).toFixed(4));
};
const percentToDecimalRate = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const pct = Number(value);
  if (!Number.isFinite(pct)) return '';
  return String(+(pct / 100).toFixed(6));
};

const PAYOUT_PERIOD_OPTIONS = [
  { value: 'weekly', label: 'Weekly (every Monday)' },
  { value: 'biweekly', label: 'Biweekly (every 2 weeks)' },
  { value: 'monthly_calendar', label: 'Monthly (calendar month)' },
  { value: 'monthly_rolling_30', label: 'Monthly (rolling 30 days)' },
  { value: 'every_n_days', label: 'Custom (every N days)' },
];

const SectionCard = ({
  gradient,
  icon: Icon,
  title,
  description,
  children,
}) => (
  <section className="rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
    <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-50/60 px-6 py-4 dark:border-slate-700 dark:bg-slate-900/40">
      <div
        className={`flex h-10 w-10 items-center justify-center rounded-md bg-gradient-to-br ${gradient} text-white shadow-sm`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
          {title}
        </h2>
        {description && (
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            {description}
          </p>
        )}
      </div>
    </div>
    <div className="px-6 py-5">{children}</div>
  </section>
);

const FieldLabel = ({ children }) => (
  <label className="block text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
    {children}
  </label>
);

const inputClass =
  'block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400/20';

const ToggleRow = ({ label, description, checked, onChange, disabled }) => (
  <div className="flex items-start justify-between gap-4 py-3">
    <div className="min-w-0">
      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
        {label}
      </p>
      {description && (
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          {description}
        </p>
      )}
    </div>
    <Switch
      disabled={disabled}
      checked={checked}
      onChange={onChange}
      className={classNames(
        checked
          ? 'bg-blue-600 dark:bg-sky-500'
          : 'bg-slate-200 dark:bg-slate-700',
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:ring-sky-400 dark:focus:ring-offset-slate-800',
      )}
    >
      <span
        aria-hidden="true"
        className={classNames(
          checked ? 'translate-x-5' : 'translate-x-0',
          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
        )}
      />
    </Switch>
  </div>
);

const Settings = ({ companyDetail, setNotf, updateCompany }) => {
  const dispatch = useDispatch();
  const settingsMap = useSelector((state) => state.companyReducer.settings);
  const [loading, setLoading] = useState({
    storeListLoading: false,
    slugUpdateLoading: false,
    settingsLoading: false,
    toggleLoading: false,
    payoutPeriodSaveLoading: false,
    earlyPayoutFeeSaveLoading: false,
    fraudSaveLoading: false,
    posMappingLoading: false,
  });
  const [storeList, setStoreList] = useState([]);
  const [payoutPaused, setPayoutPaused] = useState(false);
  const [kitchenTipsHidden, setKitchenTipsHidden] = useState(false);
  const [financeV2Enabled, setFinanceV2Enabled] = useState(false);
  const [payoutPeriodType, setPayoutPeriodType] = useState('weekly');
  const [payoutIntervalDays, setPayoutIntervalDays] = useState('');
  const [payoutAnchor, setPayoutAnchor] = useState('');
  const [payoutTimezone, setPayoutTimezone] = useState('');
  const [earlyPayoutFeeMode, setEarlyPayoutFeeMode] = useState('rate');
  const [earlyPayoutFeePercent, setEarlyPayoutFeePercent] = useState('');
  const [earlyPayoutFeeAmount, setEarlyPayoutFeeAmount] = useState('');
  const [timezoneList, setTimezoneList] = useState([]);
  const [refundPaused, setRefundPaused] = useState(false);
  const [fraudEnabled, setFraudEnabled] = useState(false);
  const [fraudMode, setFraudMode] = useState('last_x_days');
  const [fraudDays, setFraudDays] = useState('');
  const [fraudOrderCount, setFraudOrderCount] = useState('');
  const [fraudThreshold, setFraudThreshold] = useState('');

  const getStoreList = async () => {
    try {
      setLoading((prev) => ({ ...prev, storeListLoading: true }));
      const query = `companyid=${companyDetail.id}`;
      const list = await storeApi.list(query);
      if (list.data.data.length > 0) {
        setStoreList(list.data.data);
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      setLoading((prev) => ({ ...prev, storeListLoading: false }));
    }
  };

  const loadCompanySettings = async () => {
    try {
      setLoading((prev) => ({ ...prev, settingsLoading: true }));
      await dispatch(fetchCompanySettings(companyDetail.id)).unwrap();
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error || 'Failed to load company settings',
        display: true,
        type: 'error',
      }));
    } finally {
      setLoading((prev) => ({ ...prev, settingsLoading: false }));
    }
  };

  useEffect(() => {
    setPayoutPaused(settingsMap[SETTING_CODES.PAYOUT_PAUSED]?.value === 'true');
    setKitchenTipsHidden(
      settingsMap[SETTING_CODES.KITCHEN_TIPS_HIDDEN]?.value === 'true',
    );
    setFinanceV2Enabled(
      settingsMap[SETTING_CODES.FINANCE_V2_ENABLED]?.value === 'true',
    );
    setPayoutPeriodType(
      settingsMap[SETTING_CODES.PAYOUT_PERIOD_TYPE]?.value || 'weekly',
    );
    setPayoutIntervalDays(
      settingsMap[SETTING_CODES.PAYOUT_PERIOD_INTERVAL_DAYS]?.value || '',
    );
    setPayoutAnchor(
      settingsMap[SETTING_CODES.PAYOUT_PERIOD_ANCHOR]?.value || '',
    );
    setPayoutTimezone(
      settingsMap[SETTING_CODES.PAYOUT_PERIOD_TIMEZONE]?.value || '',
    );
    const storedFeeType =
      settingsMap[SETTING_CODES.EARLY_PAYOUT_FEE_TYPE]?.value;
    setEarlyPayoutFeeMode(storedFeeType === 'fixed' ? 'fixed' : 'rate');
    const storedFeeRate =
      settingsMap[SETTING_CODES.EARLY_PAYOUT_FEE_RATE]?.value;
    setEarlyPayoutFeePercent(
      storedFeeRate
        ? decimalRateToPercent(storedFeeRate)
        : String(DEFAULT_EARLY_PAYOUT_FEE_PERCENT),
    );
    setEarlyPayoutFeeAmount(
      settingsMap[SETTING_CODES.EARLY_PAYOUT_FEE_AMOUNT]?.value || '',
    );
    setRefundPaused(settingsMap[SETTING_CODES.REFUND_PAUSED]?.value === 'true');
    setFraudEnabled(
      settingsMap[SETTING_CODES.REFUND_FRAUD_ENABLED]?.value === 'true',
    );
    setFraudMode(
      settingsMap[SETTING_CODES.REFUND_FRAUD_MODE]?.value || 'last_x_days',
    );
    setFraudDays(settingsMap[SETTING_CODES.REFUND_FRAUD_DAYS]?.value || '');
    setFraudOrderCount(
      settingsMap[SETTING_CODES.REFUND_FRAUD_ORDER_COUNT]?.value || '',
    );
    setFraudThreshold(
      settingsMap[SETTING_CODES.REFUND_FRAUD_THRESHOLD_PERCENT]?.value || '',
    );
  }, [settingsMap]);

  const onToggleSetting = async (code, value, setLocalState) => {
    try {
      setLoading((prev) => ({ ...prev, toggleLoading: true }));
      setLocalState(value);
      const existing = settingsMap[code];
      if (existing) {
        await companySettingApi.update({
          id: existing.id,
          companyid: companyDetail.id,
          code,
          value: String(value),
        });
      } else {
        await companySettingApi.create({
          companyid: companyDetail.id,
          code,
          value: String(value),
        });
      }
      await dispatch(fetchCompanySettings(companyDetail.id)).unwrap();
      setNotf({
        display: true,
        type: 'success',
        message: 'Setting updated successfully',
      });
    } catch (error) {
      setLocalState(!value);
      setNotf({
        display: true,
        type: 'error',
        message: error?.response?.data?.message || 'Failed to update setting',
      });
    } finally {
      setLoading((prev) => ({ ...prev, toggleLoading: false }));
    }
  };

  const onTogglePosMappingEdit = async (val) => {
    try {
      setLoading((prev) => ({ ...prev, posMappingLoading: true }));
      const body = { ...companyDetail, allowposmappingedit: val };
      const formData = new FormData();
      Object.keys(body).forEach((k) => {
        if (k === 'allowposmappingedit') {
          formData.append(k, val);
        } else if (body[k]) {
          formData.append(k, body[k]);
        }
      });
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      await companyApi.upsert(formData, config);
      setNotf({
        display: true,
        type: 'success',
        message: 'Setting updated successfully',
      });
    } catch (error) {
      setNotf({
        display: true,
        type: 'error',
        message:
          error?.response?.data?.message || 'Failed to update setting',
      });
    } finally {
      updateCompany();
      setLoading((prev) => ({ ...prev, posMappingLoading: false }));
    }
  };

  const onSavePayoutPeriodSettings = async () => {
    try {
      setLoading((prev) => ({ ...prev, payoutPeriodSaveLoading: true }));
      const codes = [
        SETTING_CODES.PAYOUT_PERIOD_TYPE,
        SETTING_CODES.PAYOUT_PERIOD_TIMEZONE,
      ];
      if (payoutPeriodType === 'every_n_days') {
        codes.push(SETTING_CODES.PAYOUT_PERIOD_INTERVAL_DAYS);
        codes.push(SETTING_CODES.PAYOUT_PERIOD_ANCHOR);
      }
      const valueMap = {
        [SETTING_CODES.PAYOUT_PERIOD_TYPE]: payoutPeriodType,
        [SETTING_CODES.PAYOUT_PERIOD_TIMEZONE]: payoutTimezone,
        [SETTING_CODES.PAYOUT_PERIOD_INTERVAL_DAYS]: payoutIntervalDays,
        [SETTING_CODES.PAYOUT_PERIOD_ANCHOR]: payoutAnchor,
      };
      const toCreate = [];
      const toUpdate = [];
      codes.forEach((code) => {
        const existing = settingsMap[code];
        if (existing) {
          toUpdate.push({ code, value: valueMap[code] });
        } else {
          toCreate.push({ code, value: valueMap[code] });
        }
      });
      if (toCreate.length > 0) {
        await companySettingApi.batchCreate({
          companyid: companyDetail.id,
          settings: toCreate,
        });
      }
      if (toUpdate.length > 0) {
        await companySettingApi.batchUpdate({
          companyid: companyDetail.id,
          settings: toUpdate,
        });
      }
      await dispatch(fetchCompanySettings(companyDetail.id)).unwrap();
      setNotf({
        display: true,
        type: 'success',
        message: 'Payout period settings saved successfully',
      });
    } catch (error) {
      setNotf({
        display: true,
        type: 'error',
        message:
          error?.response?.data?.message ||
          'Failed to save payout period settings',
      });
    } finally {
      setLoading((prev) => ({ ...prev, payoutPeriodSaveLoading: false }));
    }
  };

  const onSaveEarlyPayoutFee = async () => {
    const codes = [SETTING_CODES.EARLY_PAYOUT_FEE_TYPE];
    const valueMap = {
      [SETTING_CODES.EARLY_PAYOUT_FEE_TYPE]: earlyPayoutFeeMode,
    };

    if (earlyPayoutFeeMode === 'fixed') {
      const amt = Number(earlyPayoutFeeAmount);
      if (!Number.isFinite(amt) || amt < 0) {
        setNotf({
          display: true,
          type: 'error',
          message: 'Early payout fee amount must be a non-negative number.',
        });
        return;
      }
      codes.push(SETTING_CODES.EARLY_PAYOUT_FEE_AMOUNT);
      valueMap[SETTING_CODES.EARLY_PAYOUT_FEE_AMOUNT] = String(amt);
    } else {
      const pct = Number(earlyPayoutFeePercent);
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
        setNotf({
          display: true,
          type: 'error',
          message: 'Early payout fee must be a percentage between 0 and 100.',
        });
        return;
      }
      codes.push(SETTING_CODES.EARLY_PAYOUT_FEE_RATE);
      valueMap[SETTING_CODES.EARLY_PAYOUT_FEE_RATE] = percentToDecimalRate(pct);
    }

    try {
      setLoading((prev) => ({ ...prev, earlyPayoutFeeSaveLoading: true }));
      const toCreate = [];
      const toUpdate = [];
      codes.forEach((code) => {
        const existing = settingsMap[code];
        if (existing) {
          toUpdate.push({ code, value: valueMap[code] });
        } else {
          toCreate.push({ code, value: valueMap[code] });
        }
      });
      if (toCreate.length > 0) {
        await companySettingApi.batchCreate({
          companyid: companyDetail.id,
          settings: toCreate,
        });
      }
      if (toUpdate.length > 0) {
        await companySettingApi.batchUpdate({
          companyid: companyDetail.id,
          settings: toUpdate,
        });
      }
      await dispatch(fetchCompanySettings(companyDetail.id)).unwrap();
      setNotf({
        display: true,
        type: 'success',
        message: 'Early payout fee saved successfully',
      });
    } catch (error) {
      setNotf({
        display: true,
        type: 'error',
        message:
          error?.response?.data?.message || 'Failed to save early payout fee',
      });
    } finally {
      setLoading((prev) => ({ ...prev, earlyPayoutFeeSaveLoading: false }));
    }
  };

  const onSaveFraudSettings = async () => {
    try {
      setLoading((prev) => ({ ...prev, fraudSaveLoading: true }));
      const codes = [
        SETTING_CODES.REFUND_FRAUD_MODE,
        SETTING_CODES.REFUND_FRAUD_THRESHOLD_PERCENT,
      ];
      if (fraudMode === 'last_x_days') {
        codes.push(SETTING_CODES.REFUND_FRAUD_DAYS);
      } else {
        codes.push(SETTING_CODES.REFUND_FRAUD_ORDER_COUNT);
      }
      const valueMap = {
        [SETTING_CODES.REFUND_FRAUD_MODE]: fraudMode,
        [SETTING_CODES.REFUND_FRAUD_THRESHOLD_PERCENT]: fraudThreshold,
        [SETTING_CODES.REFUND_FRAUD_DAYS]: fraudDays,
        [SETTING_CODES.REFUND_FRAUD_ORDER_COUNT]: fraudOrderCount,
      };
      const toCreate = [];
      const toUpdate = [];
      codes.forEach((code) => {
        const existing = settingsMap[code];
        if (existing) {
          toUpdate.push({ code, value: valueMap[code] });
        } else {
          toCreate.push({ code, value: valueMap[code] });
        }
      });
      if (toCreate.length > 0) {
        await companySettingApi.batchCreate({
          companyid: companyDetail.id,
          settings: toCreate,
        });
      }
      if (toUpdate.length > 0) {
        await companySettingApi.batchUpdate({
          companyid: companyDetail.id,
          settings: toUpdate,
        });
      }
      await dispatch(fetchCompanySettings(companyDetail.id)).unwrap();
      setNotf({
        display: true,
        type: 'success',
        message: 'Fraud detection settings saved successfully',
      });
    } catch (error) {
      setNotf({
        display: true,
        type: 'error',
        message:
          error?.response?.data?.message ||
          'Failed to save fraud detection settings',
      });
    } finally {
      setLoading((prev) => ({ ...prev, fraudSaveLoading: false }));
    }
  };

  const onClickUpdateStoreSlug = async (val, key, index) => {
    const form = { ...storeList[index] };
    form[key] = val;
    try {
      setLoading((prev) => ({ ...prev, slugUpdateLoading: true }));
      const formData = new FormData();
      for (const k of Object.keys(form)) {
        if (k === 'websiteclosedat') {
          formData.append(k, form[k] || '');
        } else {
          formData.append(k, form[k]);
        }
      }
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      await storeApi.upsert(formData, config);
      checkUpdate(true);
    } catch (error) {
      checkUpdate(false, error);
    } finally {
      getStoreList();
      setLoading((prev) => ({ ...prev, slugUpdateLoading: false }));
    }
  };

  const onChangeStoreSlug = async (val, key, index) => {
    setStoreList((prev) => {
      const list = [...prev];
      list[index][key] = val;
      return list;
    });
  };

  const checkUpdate = (val, message) => {
    if (val) {
      setNotf((prev) => ({
        ...prev,
        message: 'Store Updated Successfully',
        display: true,
        type: 'success',
      }));
    } else {
      setNotf((prev) => ({
        ...prev,
        message,
        display: true,
        type: 'error',
      }));
    }
  };

  const fetchTimezones = useCallback(async () => {
    try {
      const res = await timezoneApi.list();
      if (res.data?.length > 0) {
        const allowedCities = [
          'Adak',
          'Anchorage',
          'Los Angeles',
          'Denver',
          'Chicago',
          'New York',
        ];
        setTimezoneList(
          res.data.filter((tz) => {
            const haystack = `${tz?.displayName || ''} ${tz?.id || ''}`;
            return allowedCities.some((city) => haystack.includes(city));
          }),
        );
      }
    } catch {
      // Timezone list is non-critical
    }
  }, []);

  useEffect(() => {
    getStoreList();
    loadCompanySettings();
    fetchTimezones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoading = loading.storeListLoading || loading.settingsLoading;
  const orderBase = process.env.REACT_APP_ORDER_URL || '';

  if (isLoading) {
    return (
      <div className="space-y-5">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="rounded-md border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800"
          >
            <div className="flex items-center gap-3">
              <SkeletonBlock className="h-10 w-10 !rounded-md" />
              <SkeletonBlock className="h-5 w-40" />
            </div>
            <div className="mt-4 space-y-3">
              <SkeletonBlock className="h-10 w-full" />
              <SkeletonBlock className="h-10 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Store Slugs */}
      <SectionCard
        gradient="from-blue-500 to-indigo-600"
        icon={LinkIcon}
        title="Store Slugs"
        description="Customize the public URL slug for each store. Lowercase letters, numbers, and hyphens only, up to 30 characters."
      >
        {storeList.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            No stores available.
          </p>
        ) : (
          <div className="space-y-5">
            {storeList.map((store, index) => (
              <div key={store.id || index}>
                <FieldLabel>{store.name || 'Store'}</FieldLabel>
                <div className="mt-2 flex rounded-md shadow-sm ring-1 ring-slate-200 transition focus-within:ring-2 focus-within:ring-blue-500/30 dark:ring-slate-600 dark:focus-within:ring-sky-400/30">
                  <span className="inline-flex items-center rounded-l-xl border-r border-slate-200 bg-slate-50 px-3 text-xs text-slate-500 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-400">
                    {orderBase}/
                  </span>
                  <input
                    value={store.slug || ''}
                    type="text"
                    className="block w-full flex-1 rounded-r-xl border-0 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-0 dark:bg-slate-900/40 dark:text-slate-100 dark:placeholder-slate-500"
                    placeholder="thisway"
                    onBlur={(e) =>
                      onClickUpdateStoreSlug(e.target.value, 'slug', index)
                    }
                    maxLength={SLUG_MAX_LENGTH}
                    onChange={(e) =>
                      onChangeStoreSlug(toSlug(e.target.value), 'slug', index)
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Payout */}
      <SectionCard
        gradient="from-emerald-500 to-teal-600"
        icon={BanknotesIcon}
        title="Payout"
        description="Control payout scheduling and the finance experience."
      >
        <div className="divide-y divide-slate-200 dark:divide-slate-700">
          <ToggleRow
            label="Pause Payout"
            description="Temporarily pause payouts for this company."
            checked={payoutPaused}
            onChange={(val) =>
              onToggleSetting(SETTING_CODES.PAYOUT_PAUSED, val, setPayoutPaused)
            }
            disabled={loading.toggleLoading}
          />
          <ToggleRow
            label="Ledger-Based Payout Balance"
            description="Calculate this company's payout balance from Thisway's own ledger (PayoutFinanceV2) instead of reading it straight from Stripe."
            checked={financeV2Enabled}
            onChange={(val) =>
              onToggleSetting(
                SETTING_CODES.FINANCE_V2_ENABLED,
                val,
                setFinanceV2Enabled,
              )
            }
            disabled={loading.toggleLoading}
          />
        </div>

        <div className="mt-5 rounded-md border border-slate-200 bg-slate-50/60 p-5 dark:border-slate-700 dark:bg-slate-900/40">
          <div className="flex items-center gap-2 text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
            <ClockIcon className="h-3.5 w-3.5" />
            Payout Period
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <FieldLabel>Period Type</FieldLabel>
              <select
                value={payoutPeriodType}
                onChange={(e) => setPayoutPeriodType(e.target.value)}
                className={`mt-2 ${inputClass}`}
              >
                {PAYOUT_PERIOD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <FieldLabel>
                <span className="inline-flex items-center gap-1">
                  <GlobeAltIcon className="h-3 w-3" />
                  Timezone
                </span>
              </FieldLabel>
              <Select
                className="company-detail-select mt-2 w-full"
                placeholder="Select timezone (blank = UTC)"
                value={payoutTimezone || undefined}
                onChange={(val) => setPayoutTimezone(val || '')}
                allowClear
                showSearch
                optionFilterProp="label"
                options={timezoneList.map((tz) => ({
                  label: tz.displayName,
                  value: tz.id,
                }))}
              />
            </div>

            {payoutPeriodType === 'every_n_days' && (
              <>
                <div>
                  <FieldLabel>Interval (days)</FieldLabel>
                  <input
                    type="number"
                    min="1"
                    value={payoutIntervalDays}
                    onChange={(e) => setPayoutIntervalDays(e.target.value)}
                    placeholder="e.g. 7"
                    className={`mt-2 ${inputClass}`}
                  />
                </div>
                <div>
                  <FieldLabel>
                    <span className="inline-flex items-center gap-1">
                      <CalendarDaysIcon className="h-3 w-3" />
                      Anchor Date
                    </span>
                  </FieldLabel>
                  <input
                    type="date"
                    value={payoutAnchor}
                    onChange={(e) => setPayoutAnchor(e.target.value)}
                    className={`mt-2 ${inputClass}`}
                  />
                </div>
              </>
            )}
          </div>

          <div className="mt-5 flex justify-end">
            <button
              type="button"
              disabled={loading.payoutPeriodSaveLoading}
              onClick={onSavePayoutPeriodSettings}
              className="inline-flex h-10 items-center justify-center rounded-md border-none bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-sky-500 dark:hover:bg-sky-600"
            >
              {loading.payoutPeriodSaveLoading
                ? 'Saving…'
                : 'Save Payout Period'}
            </button>
          </div>
        </div>

        <div className="mt-5 rounded-md border border-slate-200 bg-slate-50/60 p-5 dark:border-slate-700 dark:bg-slate-900/40">
          <div className="flex items-center gap-2 text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
            <BanknotesIcon className="h-3.5 w-3.5" />
            Early Payout Fee
          </div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Choose whether the fee is a percentage of the payout amount or a
            flat dollar amount. Default is {DEFAULT_EARLY_PAYOUT_FEE_PERCENT}%
            when no value is set.
          </p>

          <div className="mt-4">
            <FieldLabel>Fee Type</FieldLabel>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {[
                { value: 'rate', label: 'Percentage (%)' },
                { value: 'fixed', label: 'Fixed Amount ($)' },
              ].map((opt) => {
                const active = earlyPayoutFeeMode === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setEarlyPayoutFeeMode(opt.value)}
                    className={classNames(
                      'flex h-10 items-center justify-center rounded-md border text-sm font-medium transition',
                      active
                        ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-sky-400 dark:bg-sky-400/10 dark:text-sky-300'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700/60',
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {earlyPayoutFeeMode === 'fixed' ? (
              <div>
                <FieldLabel>Fee Amount</FieldLabel>
                <div className="relative mt-2">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">
                    $
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={earlyPayoutFeeAmount}
                    onChange={(e) => setEarlyPayoutFeeAmount(e.target.value)}
                    placeholder="e.g. 20"
                    className={`${inputClass} pl-7`}
                  />
                </div>
                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                  Charged as a flat fee per early payout regardless of the
                  payout amount.
                </p>
              </div>
            ) : (
              <div>
                <FieldLabel>Fee Rate</FieldLabel>
                <div className="relative mt-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={earlyPayoutFeePercent}
                    onChange={(e) => setEarlyPayoutFeePercent(e.target.value)}
                    placeholder={`e.g. ${DEFAULT_EARLY_PAYOUT_FEE_PERCENT}`}
                    className={`${inputClass} pr-8`}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">
                    %
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                  A percentage of the requested early payout amount.
                </p>
              </div>
            )}
          </div>

          <div className="mt-5 flex justify-end">
            <button
              type="button"
              disabled={loading.earlyPayoutFeeSaveLoading}
              onClick={onSaveEarlyPayoutFee}
              className="inline-flex h-10 items-center justify-center rounded-md border-none bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-sky-500 dark:hover:bg-sky-600"
            >
              {loading.earlyPayoutFeeSaveLoading
                ? 'Saving…'
                : 'Save Early Payout Fee'}
            </button>
          </div>
        </div>
      </SectionCard>

      {/* Refund */}
      <SectionCard
        gradient="from-amber-500 to-orange-600"
        icon={ArrowUturnLeftIcon}
        title="Refund"
        description="Pause or resume refund processing."
      >
        <ToggleRow
          label="Pause Refund"
          description="Temporarily pause refunds for this company."
          checked={refundPaused}
          onChange={(val) =>
            onToggleSetting(SETTING_CODES.REFUND_PAUSED, val, setRefundPaused)
          }
          disabled={loading.toggleLoading}
        />
      </SectionCard>

      {/* Refund Fraud Detection */}
      <SectionCard
        gradient="from-rose-500 to-red-600"
        icon={ShieldCheckIcon}
        title="Refund Fraud Detection"
        description="Automatically detect suspicious refund activity."
      >
        <ToggleRow
          label="Enable Fraud Detection"
          description="Flag refund behavior that exceeds the threshold."
          checked={fraudEnabled}
          onChange={(val) =>
            onToggleSetting(
              SETTING_CODES.REFUND_FRAUD_ENABLED,
              val,
              setFraudEnabled,
            )
          }
          disabled={loading.toggleLoading}
        />

        {fraudEnabled && (
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50/60 p-5 dark:border-slate-700 dark:bg-slate-900/40">
            <FieldLabel>Detection Mode</FieldLabel>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {[
                { value: 'last_x_days', label: 'Last X Days' },
                { value: 'last_x_orders', label: 'Last X Orders' },
              ].map((opt) => {
                const active = fraudMode === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFraudMode(opt.value)}
                    className={classNames(
                      'flex h-10 items-center justify-center rounded-md border text-sm font-medium transition',
                      active
                        ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-sky-400 dark:bg-sky-400/10 dark:text-sky-300'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700/60',
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {fraudMode === 'last_x_days' ? (
                <div>
                  <FieldLabel>Number of Days</FieldLabel>
                  <input
                    type="number"
                    min="1"
                    value={fraudDays}
                    onChange={(e) => setFraudDays(e.target.value)}
                    placeholder="e.g. 30"
                    className={`mt-2 ${inputClass}`}
                  />
                </div>
              ) : (
                <div>
                  <FieldLabel>Number of Orders</FieldLabel>
                  <input
                    type="number"
                    min="1"
                    value={fraudOrderCount}
                    onChange={(e) => setFraudOrderCount(e.target.value)}
                    placeholder="e.g. 50"
                    className={`mt-2 ${inputClass}`}
                  />
                </div>
              )}

              <div>
                <FieldLabel>Threshold Percentage</FieldLabel>
                <div className="relative mt-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={fraudThreshold}
                    onChange={(e) => setFraudThreshold(e.target.value)}
                    placeholder="e.g. 25"
                    className={`${inputClass} pr-8`}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">
                    %
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                disabled={loading.fraudSaveLoading}
                onClick={onSaveFraudSettings}
                className="inline-flex h-10 items-center justify-center rounded-md border-none bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-sky-500 dark:hover:bg-sky-600"
              >
                {loading.fraudSaveLoading ? 'Saving…' : 'Save Fraud Settings'}
              </button>
            </div>
          </div>
        )}
      </SectionCard>

      {/* POS Integrations */}
      <SectionCard
        gradient="from-slate-500 to-slate-700"
        icon={PuzzlePieceIcon}
        title="POS Integrations"
        description="Control who can edit Square POS mapping."
      >
        <ToggleRow
          label="Allow Restaurant POS Mapping Edit"
          description="When on, store owners and managers can edit Square POS mapping on products, items and modifiers. When off, only admins can."
          checked={!!companyDetail.allowposmappingedit}
          onChange={onTogglePosMappingEdit}
          disabled={loading.posMappingLoading}
        />
      </SectionCard>

      {/* Kitchen App */}
      <SectionCard
        gradient="from-orange-500 to-amber-600"
        icon={DevicePhoneMobileIcon}
        title="Kitchen App"
        description="Control what this company's kitchen terminals show."
      >
        <ToggleRow
          label="Hide Tips Screen"
          description="Hides the Tips report page in the kitchen terminal app for all of this company's stores. Takes effect when the app is restarted."
          checked={kitchenTipsHidden}
          onChange={(val) =>
            onToggleSetting(
              SETTING_CODES.KITCHEN_TIPS_HIDDEN,
              val,
              setKitchenTipsHidden,
            )
          }
          disabled={loading.toggleLoading}
        />
      </SectionCard>
    </div>
  );
};

export default Settings;
